import { Chapter } from 'interfaces/ServiceWork';
import { Status, VisitStatusOptions, WorkStatus } from 'interfaces/Work';
import { Visit } from 'interfaces/ChartItem';
import React, { createContext, FunctionComponent, useState } from 'react';

interface WorkData {
  id: number;
  name: string;
  short_name: string;
  atx_lines: any[];
  __typename: string;
  isCollapsed?: boolean;
}

interface SelectedAnalysisImageIndex {
  [analysisId: number]: number;
}

interface WorkContextType {
  formPayload: Chapter[];
  setFormPayload: (formPayload: Chapter[]) => void;
  withoutServiceModel: boolean;
  setWithoutServiceModel: (withoutServiceModel: boolean) => void;
  visitStatusOptions?: VisitStatusOptions[] | null;
  setVisitStatusOptions?: (visitStatusOptions: VisitStatusOptions[]) => void;
  refreshCount: number;
  triggerRefresh: () => void;
  workStatus: WorkStatus;
  setWorkStatus: (workStatus: WorkStatus) => void;
  getStatuses: () => Status[];
  getStatusById: (id: number) => Status | undefined;
  getStatusColor: (id: number) => string | undefined;
  workDataPDF: WorkData[] | null;
  setWorkDataPDF: (workDataPDF: WorkData[] | null) => void;
  visitsVisible: Visit[];
  setVisitsVisible: React.Dispatch<React.SetStateAction<Visit[]>>;
  selectedAnalysisImageIndexes: SelectedAnalysisImageIndex;
  setSelectedAnalysisImageIndex: (analysisId: number, index: number) => void;
  hasActiveWorkFilters: boolean;
  setHasActiveWorkFilters: (value: boolean) => void;
}

const WorkContext = createContext<WorkContextType>({
  formPayload: [],
  setFormPayload: () => null,
  withoutServiceModel: false,
  setWithoutServiceModel: () => null,
  visitStatusOptions: null,
  setVisitStatusOptions: () => null,
  refreshCount: 0,
  triggerRefresh: () => {},
  workStatus: null,
  setWorkStatus: () => null,
  getStatuses: () => null,
  getStatusById: () => null,
  getStatusColor: () => null,
  workDataPDF: null,
  setWorkDataPDF: () => null,
  visitsVisible: [],
  setVisitsVisible: () => null,
  selectedAnalysisImageIndexes: {},
  setSelectedAnalysisImageIndex: () => null,
  hasActiveWorkFilters: false,
  setHasActiveWorkFilters: () => null,
});

const WorkProvider: FunctionComponent<{}> = ({ children }) => {
  const [formPayload, setFormPayload] = useState<Chapter[]>([]);
  const [withoutServiceModel, setWithoutServiceModel] = useState(false);
  const [visitStatusOptions, setVisitStatusOptions] = useState<VisitStatusOptions[]>(null);
  const [refreshCount, setRefreshCount] = useState(0);
  const [workStatus, setWorkStatus] = useState<WorkStatus>(null);
  const [workDataPDF, setWorkDataPDF] = useState<WorkData[]>(null);
  const [visitsVisible, setVisitsVisible] = useState<Visit[]>([]);
  const [
    selectedAnalysisImageIndexes,
    setSelectedAnalysisImageIndexes,
  ] = useState<SelectedAnalysisImageIndex>({});
  const [hasActiveWorkFilters, setHasActiveWorkFilters] = useState<boolean>(false);

  const setSelectedAnalysisImageIndex = (analysisId: number, index: number) => {
    setSelectedAnalysisImageIndexes((prev) => ({
      ...prev,
      [analysisId]: index,
    }));
  };

  const getStatuses = (): Status[] => {
    return workStatus?.statuses || [];
  };

  const getStatusById = (id: number): Status | undefined => {
    return workStatus?.statuses.find((status) => status.id === id);
  };

  const getStatusColor = (id: number): string | undefined => {
    const status = getStatusById(id);
    return status?.color;
  };

  const triggerRefresh = () => {
    setRefreshCount((prev) => prev + 1);
  };

  return (
    <WorkContext.Provider
      value={{
        formPayload,
        setFormPayload,
        withoutServiceModel,
        setWithoutServiceModel,
        visitStatusOptions,
        setVisitStatusOptions,
        refreshCount,
        triggerRefresh,
        workStatus,
        setWorkStatus,
        getStatuses,
        getStatusById,
        getStatusColor,
        workDataPDF,
        setWorkDataPDF,
        visitsVisible,
        setVisitsVisible,
        selectedAnalysisImageIndexes,
        setSelectedAnalysisImageIndex,
        hasActiveWorkFilters,
        setHasActiveWorkFilters,
      }}
    >
      {children}
    </WorkContext.Provider>
  );
};

export { WorkContext, WorkProvider };
